import { formatDate, formatUsd } from '@ytclip/shared'
import { CheckCircle2, Mail, ShieldCheck, User } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/card.tsx'
import { Badge, ErrorState, Skeleton } from '@/components/ui/feedback.tsx'
import { PageHeader } from '@/components/ui/misc.tsx'
import { useBalance } from '@/features/shared-hooks.ts'
import { useCurrentUser } from '@/features/user/hooks.ts'

export function ProfilePage() {
  const { data, isPending, isError } = useCurrentUser()
  const balance = useBalance()

  return (
    <>
      <PageHeader title="Profil" description="Informasi akun kamu" />

      <Card>
        <CardHeader title="Akun" icon={<User className="size-5" />} />
        <CardBody className="space-y-6 pt-4">
          {isError ? (
            <ErrorState message="Profil tidak bisa dimuat." />
          ) : isPending ? (
            <div className="flex items-center gap-4">
              <Skeleton className="size-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4">
                {data.avatarUrl ? (
                  <img
                    src={data.avatarUrl}
                    alt=""
                    className="size-16 rounded-full object-cover"
                  />
                ) : (
                  <span className="inline-flex size-16 items-center justify-center rounded-full bg-primary-container">
                    <User className="size-7 text-on-primary-container" />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-title-lg font-medium text-on-surface">{data.name}</p>
                  <p className="text-body-md text-on-surface-variant">
                    Bergabung {formatDate(data.createdAt)}
                  </p>
                </div>
              </div>

              <div className="border-t border-outline-variant pt-6">
                <p className="flex items-center gap-2 text-label-md font-medium text-on-surface-variant">
                  <Mail className="size-4" aria-hidden="true" />
                  Email
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-body-md text-on-surface">{data.email}</span>
                  <Badge tone="success">
                    <CheckCircle2 className="size-3" aria-hidden="true" />
                    Terverifikasi
                  </Badge>
                </div>
              </div>

              <div className="grid gap-4 border-t border-outline-variant pt-6 sm:grid-cols-2">
                <Detail
                  label="Saldo"
                  value={balance.data ? formatUsd(balance.data.balanceUsd) : '—'}
                />
                <Detail
                  label="Total terpakai"
                  value={balance.data ? formatUsd(balance.data.spentUsd) : '—'}
                />
              </div>

              {data.isSuperAdmin ? (
                <div className="flex items-start gap-3 rounded-md bg-primary-container p-4 text-on-primary-container">
                  <ShieldCheck className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                  <p className="text-body-md">
                    Akun ini punya akses super admin. Gunakan pemilih mode di sidebar untuk
                    berpindah antara dashboard dan panel admin.
                  </p>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface-container-low p-4">
      <p className="text-label-sm text-on-surface-variant">{label}</p>
      <p className="mt-1 text-title-lg font-medium text-on-surface">{value}</p>
    </div>
  )
}
